'use client';

import type { Job } from '@/types/jobs';
import type { Organisation } from '@/types/organisation';
import type { ShareData } from '@/types/share';

/**
 * Creates share data for a job posting
 *
 * @param job - The job details
 * @param organisation - The organization details
 * @param url - The URL to share (defaults to current URL)
 * @returns ShareData object for use with ShareButtons
 */
export const createJobShareData = (
  job: Job,
  organisation: Organisation,
  url: string = ''
): ShareData => {
  // Get the current URL if not provided and if we're in a browser environment
  const shareUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/careers/${organisation.id}/jobs/${job.id}`;

  // Format the share content according to the specified format
  const title = `${organisation.name} is hiring!`;

  const description = `Role: ${job.title} 
Location: ${job.locationTypeLabel || 'On-site'}${job.location ? ` (${job.location})` : ''}
Work Experience: ${job.workExperience?.minimum || 0}-${job.workExperience?.maximum || 1} years

Apply Now: ${shareUrl}`;

  return {
    title,
    description,
    url: shareUrl,
  };
};

/**
 * Creates share data for an organization and its open positions
 *
 * @param organisation - The organization details
 * @param jobs - List of jobs to include in the share content
 * @param url - The URL to share (defaults to current URL)
 * @returns ShareData object for use with ShareButtons
 */
export const createOrganizationShareData = (
  organisation: Organisation,
  jobs: Job[],
  url: string = ''
): ShareData => {
  // Get the current URL if not provided and if we're in a browser environment
  const shareUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/careers/${organisation.id}`;

  // Format the share content according to the specified format
  const title = `${organisation.name} is hiring!`;

  const positionsList = jobs
    .map(
      job =>
        `${job.title} (${job.locationTypeLabel || 'On-site'}${job.location ? `, ${job.location}` : ''})`
    )
    .join('\n');

  const description = `Open Positions:

${positionsList}

${shareUrl}`;

  return {
    title,
    description,
    url: shareUrl,
  };
};

const REFERRAL_MESSAGE =
  'Hey! I thought you might be a great fit for this role. Check it out and apply using my link:';

/**
 * Creates share data for the referral flow (Refer someone modal).
 * Same message for WhatsApp, LinkedIn, X, and Copy per RFC-2026-007.
 *
 * @param referralUrl - Full URL to candidate app with UTM params (jobId, source=referral, medium=job_page, campaign=email)
 * @returns ShareData for ShareButtons
 */
export const createReferralShareData = (referralUrl: string): ShareData => ({
  title: REFERRAL_MESSAGE,
  description: referralUrl,
  url: referralUrl,
});

/**
 * Full referral message (title + description) for copy/share.
 */
export const getReferralShareMessage = (referralUrl: string): string =>
  `${REFERRAL_MESSAGE}\n\n${referralUrl}`;

/**
 * Copies text to clipboard
 *
 * @param text - Text to copy to clipboard
 * @returns Promise that resolves when the text is copied
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    console.error('Clipboard API not available');
    return false;
  }

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text: ', err);
    return false;
  }
};
